import { buildChainDiagramPaths } from '../../lib/mapping/roleChainRelationships';
import {
  buildHandDiagram,
  buildPrimaryHandRoleNameMap,
} from './handDiagramTemplates';
import type {
  BoneAssignmentChainDiagramPath,
  BoneAssignmentDiagram,
  BoneAssignmentDiagramConnection,
  BoneAssignmentDiagramNode,
} from './types';

const BODY_DIAGRAM_NODES: readonly BoneAssignmentDiagramNode[] = [
  { roleName: 'Head', shortLabel: 'Head', x: 50, y: 16, size: 'lg' },
  { roleName: 'Neck', shortLabel: 'Neck', x: 50, y: 23, size: 'xs' },
  { roleName: 'Chest', shortLabel: 'Chest', x: 50, y: 29, size: 'md' },
  { roleName: 'Spine', shortLabel: 'Spine', x: 50, y: 39, size: 'md' },
  { roleName: 'Pelvis', shortLabel: 'Pelvis', x: 50, y: 48, size: 'lg' },
  { roleName: 'Root', shortLabel: 'Root', x: 50, y: 83, size: 'md' },

  { roleName: 'LeftClavicle', shortLabel: 'Clav', x: 44, y: 27.5, size: 'xs' },
  { roleName: 'LeftUpperArm', shortLabel: 'UArm', x: 38, y: 27, size: 'md' },
  { roleName: 'LeftLowerArm', shortLabel: 'LArm', x: 33, y: 37, size: 'sm' },
  { roleName: 'LeftHand', shortLabel: 'LHand', x: 30, y: 50, size: 'sm' },
  { roleName: 'RightClavicle', shortLabel: 'Clav', x: 56, y: 27.5, size: 'xs' },
  { roleName: 'RightUpperArm', shortLabel: 'UArm', x: 62, y: 27, size: 'md' },
  { roleName: 'RightLowerArm', shortLabel: 'LArm', x: 67, y: 37, size: 'sm' },
  { roleName: 'RightHand', shortLabel: 'RHand', x: 70, y: 50, size: 'sm' },

  { roleName: 'LeftUpperLeg', shortLabel: 'ULeg', x: 44, y: 52, size: 'sm' },
  { roleName: 'LeftLowerLeg', shortLabel: 'LLeg', x: 41, y: 67, size: 'sm' },
  { roleName: 'LeftHock', shortLabel: 'Hock', x: 39.5, y: 75, size: 'xs' },
  { roleName: 'LeftFoot', shortLabel: 'Foot', x: 38, y: 83, size: 'md' },
  { roleName: 'LeftToe', shortLabel: 'Toe', x: 31, y: 85, size: 'xs' },
  { roleName: 'RightUpperLeg', shortLabel: 'ULeg', x: 56, y: 52, size: 'sm' },
  { roleName: 'RightLowerLeg', shortLabel: 'LLeg', x: 59, y: 67, size: 'sm' },
  { roleName: 'RightHock', shortLabel: 'Hock', x: 60.5, y: 75, size: 'xs' },
  { roleName: 'RightFoot', shortLabel: 'Foot', x: 62, y: 83, size: 'md' },
  { roleName: 'RightToe', shortLabel: 'Toe', x: 69, y: 85, size: 'xs' },
] as const;

const BODY_DIAGRAM_CONNECTIONS: readonly BoneAssignmentDiagramConnection[] = [
  { from: 'Head', to: 'Neck' },
  { from: 'Neck', to: 'Chest' },
  { from: 'Chest', to: 'Spine' },
  { from: 'Spine', to: 'Pelvis' },

  { from: 'Chest', to: 'LeftClavicle' },
  { from: 'LeftClavicle', to: 'LeftUpperArm' },
  { from: 'LeftUpperArm', to: 'LeftLowerArm' },
  { from: 'LeftLowerArm', to: 'LeftHand' },

  { from: 'Chest', to: 'RightClavicle' },
  { from: 'RightClavicle', to: 'RightUpperArm' },
  { from: 'RightUpperArm', to: 'RightLowerArm' },
  { from: 'RightLowerArm', to: 'RightHand' },
  
  { from: 'Pelvis', to: 'LeftUpperLeg' },
  { from: 'LeftUpperLeg', to: 'LeftLowerLeg' },
  { from: 'LeftLowerLeg', to: 'LeftHock' },
  { from: 'LeftHock', to: 'LeftFoot' },
  { from: 'LeftFoot', to: 'LeftToe' },
  { from: 'Pelvis', to: 'RightUpperLeg' },
  { from: 'RightUpperLeg', to: 'RightLowerLeg' },
  { from: 'RightLowerLeg', to: 'RightHock' },
  { from: 'RightHock', to: 'RightFoot' },
  { from: 'RightFoot', to: 'RightToe' },
] as const;

function mirrorNodes(
  nodes: readonly BoneAssignmentDiagramNode[],
): readonly BoneAssignmentDiagramNode[] {
  return nodes.map((node) => ({
    ...node,
    x: 100 - node.x,
  }));
}

export const BODY_ASSIGNMENT_DIAGRAM: BoneAssignmentDiagram = {
  key: 'body',
  label: 'Biped',
  minHeightPx: 400,
  maxWidthPx: 270,
  contentBounds: {
    left: 14,
    right: 86,
    top: 6,
    bottom: 92,
  },
  nodes: mirrorNodes(BODY_DIAGRAM_NODES),
  connections: BODY_DIAGRAM_CONNECTIONS,
};

export const HAND_ASSIGNMENT_DIAGRAMS: readonly BoneAssignmentDiagram[] = [
  buildHandDiagram({
    key: 'right-hand',
    label: 'Right',
    side: 'right',
    roleNameByTemplateRole: buildPrimaryHandRoleNameMap('right'),
  }),
  buildHandDiagram({
    key: 'left-hand',
    label: 'Left',
    side: 'left',
    roleNameByTemplateRole: buildPrimaryHandRoleNameMap('left'),
  }),
] as const;

export const BONE_ASSIGNMENT_DIAGRAMS: readonly BoneAssignmentDiagram[] = [
  BODY_ASSIGNMENT_DIAGRAM,
  ...HAND_ASSIGNMENT_DIAGRAMS,
];

export const BONE_ASSIGNMENT_ROLE_NAMES = BONE_ASSIGNMENT_DIAGRAMS.flatMap(
  (diagram) => diagram.nodes.map((node) => node.roleName),
);

export const BONE_ASSIGNMENT_CHAIN_PATHS: readonly BoneAssignmentChainDiagramPath[] =
  buildChainDiagramPaths();

export const BONE_ASSIGNMENT_CHAIN_NAMES = BONE_ASSIGNMENT_CHAIN_PATHS.map(
  (chainPath) => chainPath.chainName,
);

export const BONE_ASSIGNMENT_CHAIN_NAME_SET = new Set(
  BONE_ASSIGNMENT_CHAIN_NAMES,
);
